import React, { useEffect } from 'react';
import {
  X, Camera, Radio, Share2, Search, Flame, ShieldAlert,
  Compass, AlertTriangle, Check
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface MobileMapToolsSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onShareView: () => void;
  shareCopied: boolean;
  onCreateFromPhoto?: () => void;
  trackingStatus?: 'idle' | 'recording' | 'paused';
  onStartTracking?: () => void;
  onOpenMyTracks?: () => void;
  showSearchButton: boolean;
  onSearchThisArea: () => void;
  showFireDangerButton: boolean;
  onOpenFireDanger?: () => void;
  isAdmin: boolean;
  adminBadgeCount?: number;
  onOpenAdminInbox?: () => void;
}

export const MobileMapToolsSheet: React.FC<MobileMapToolsSheetProps> = ({
  isOpen,
  onClose,
  onShareView,
  shareCopied,
  onCreateFromPhoto,
  trackingStatus = 'idle',
  onStartTracking,
  onOpenMyTracks,
  showSearchButton,
  onSearchThisArea,
  showFireDangerButton,
  onOpenFireDanger,
  isAdmin,
  adminBadgeCount = 0,
  onOpenAdminInbox,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[5500] flex flex-col justify-end bg-black/60 backdrop-blur-sm animate-in fade-in duration-200 pointer-events-auto">
      <div 
        className="fixed inset-0 -z-10" 
        onClick={onClose} 
        aria-hidden="true" 
      />

      <div 
        className="w-full bg-[var(--bg-surface)] border-t border-[var(--border-secondary)] rounded-t-3xl shadow-2xl p-5 space-y-4 max-h-[75vh] overflow-y-auto animate-in slide-in-from-bottom duration-200"
        style={{ paddingBottom: 'calc(var(--safe-area-bottom, 0px) + 20px)' }}
      >
        
        {/* Header */}
        <div className="flex items-center justify-between pb-2 border-b border-[var(--border-primary)]">
          <div>
            <h3 className="text-sm font-black text-[var(--text-primary)] uppercase tracking-wide">
              {t('map.tools_heading', 'Handlinger & Kartverktøy')}
            </h3>
            <p className="text-3xs text-[var(--text-muted)]">
              {t('map.tools_subheading', 'Snarveier for tur, foto og deling')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-xl text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-alt)] transition-colors"
            aria-label={t('common.close', 'Lukk')}
          >
            <X size={18} />
          </button>
        </div>

        {/* Action Grid */}
        <div className="grid grid-cols-2 gap-2.5">

          {/* 1. Opprett fra foto */}
          {onCreateFromPhoto && (
            <button
              type="button"
              onClick={() => {
                onClose();
                onCreateFromPhoto();
              }}
              className="flex flex-col items-center justify-center p-3.5 rounded-2xl bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] hover:border-brand/40 active:scale-[0.98] transition text-center gap-1.5"
            >
              <div className="w-10 h-10 rounded-xl bg-emerald-500/15 text-emerald-400 flex items-center justify-center">
                <Camera size={18} />
              </div>
              <span className="text-xs font-bold text-[var(--text-primary)]">
                {t('add_spot.from_photo', 'Opprett fra bilde')}
              </span>
              <span className="text-4xs text-[var(--text-muted)]">
                {t('add_spot.auto_gps', 'Automatisk GPS')}
              </span>
            </button>
          )}

          {/* 2. Turlogg & sporing */}
          <button
            type="button"
            onClick={() => {
              onClose();
              if (trackingStatus === 'idle') {
                onStartTracking?.();
              } else {
                onOpenMyTracks?.();
              }
            }}
            className={`flex flex-col items-center justify-center p-3.5 rounded-2xl border active:scale-[0.98] transition text-center gap-1.5 ${
              trackingStatus === 'recording'
                ? 'bg-accent/15 border-accent text-accent'
                : 'bg-[var(--bg-surface-alt)] border-[var(--border-primary)] hover:border-sky-500/40 text-[var(--text-primary)]'
            }`}
          >
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              trackingStatus === 'recording'
                ? 'bg-accent text-stone-900 animate-pulse'
                : 'bg-sky-500/15 text-sky-400'
            }`}>
              <Radio size={18} />
            </div>
            <span className="text-xs font-bold">
              {trackingStatus === 'recording' ? t('map.tracking_active', 'Sporer tur...') : t('auto.map.turlogg', 'Turlogg & GPS')}
            </span>
            <span className="text-4xs text-[var(--text-muted)]">
              {trackingStatus === 'recording' ? t('auto.map.trykk_for_mine_turer', 'Trykk for oversikt') : t('map.start_tracking', 'Start sporing')}
            </span>
          </button>

          {/* 3. Del kartutsnitt */}
          <button
            type="button"
            onClick={() => {
              onShareView();
            }}
            className={`flex flex-col items-center justify-center p-3.5 rounded-2xl border active:scale-[0.98] transition text-center gap-1.5 ${
              shareCopied
                ? 'bg-purple-500/20 border-purple-500 text-purple-300'
                : 'bg-[var(--bg-surface-alt)] border-[var(--border-primary)] hover:border-purple-500/40 text-[var(--text-primary)]'
            }`}
          >
            <div className="w-10 h-10 rounded-xl bg-purple-500/15 text-purple-400 flex items-center justify-center">
              {shareCopied ? <Check size={18} /> : <Share2 size={18} />}
            </div>
            <span className="text-xs font-bold">
              {shareCopied ? t('map.share_view_copied', 'Lenke kopiert!') : t('map.share_view', 'Del kartutsnitt')}
            </span>
            <span className="text-4xs text-[var(--text-muted)]">
              {t('map.share_view_sub', 'Kopier visning')}
            </span>
          </button>

          {/* 4. Søk i dette området */}
          <button
            type="button"
            disabled={!showSearchButton}
            onClick={() => {
              onClose();
              onSearchThisArea();
            }}
            className={`flex flex-col items-center justify-center p-3.5 rounded-2xl border active:scale-[0.98] transition text-center gap-1.5 ${
              showSearchButton
                ? 'bg-[var(--bg-surface-alt)] border-amber-500/40 text-[var(--text-primary)] hover:border-amber-500'
                : 'bg-[var(--bg-surface-alt)] border-[var(--border-primary)] opacity-50 cursor-not-allowed text-[var(--text-muted)]'
            }`}
          >
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              showSearchButton ? 'bg-amber-500/20 text-amber-400' : 'bg-[var(--bg-input)] text-[var(--text-muted)]'
            }`}>
              <Search size={18} />
            </div>
            <span className="text-xs font-bold">
              {t('map.search_here', 'Søk i området')}
            </span>
            <span className="text-4xs text-[var(--text-muted)]">
              {showSearchButton ? t('map.new_spots', 'Oppdater plasser') : t('map.area_updated', 'Området er oppdatert')}
            </span>
          </button>

          {/* 5. Brannfarevarsel (hvis relevant) */}
          {showFireDangerButton && (
            <button
              type="button"
              onClick={() => {
                onClose();
                onOpenFireDanger?.();
              }}
              className="col-span-2 flex items-center gap-3 p-3.5 rounded-2xl bg-amber-500/15 border border-amber-500/40 text-amber-300 active:scale-[0.98] transition"
            >
              <div className="w-10 h-10 rounded-xl bg-amber-500/25 text-amber-400 flex items-center justify-center shrink-0">
                <Flame size={20} />
              </div>
              <div className="text-left min-w-0">
                <p className="text-xs font-black">{t('fire_danger.button_title', 'Farevarsel / Brannfare')}</p>
                <p className="text-3xs text-amber-300/80 truncate">{t('fire_danger.check_zone', 'Sjekk restriksjoner i området')}</p>
              </div>
            </button>
          )}

          {/* 6. Admin innboks (hvis admin) */}
          {isAdmin && onOpenAdminInbox && (
            <button
              type="button"
              onClick={() => {
                onClose();
                onOpenAdminInbox();
              }}
              className="col-span-2 flex items-center justify-between p-3.5 rounded-2xl bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] hover:border-brand/40 active:scale-[0.98] transition"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-rose-500/15 text-rose-400 flex items-center justify-center">
                  <ShieldAlert size={18} />
                </div>
                <div className="text-left">
                  <p className="text-xs font-bold text-[var(--text-primary)]">Admin Innboks</p>
                  <p className="text-4xs text-[var(--text-muted)]">Verifiseringer og varsler</p>
                </div>
              </div>
              {adminBadgeCount > 0 && (
                <span className="px-2 py-0.5 rounded-full bg-rose-500 text-white text-3xs font-black">
                  {adminBadgeCount}
                </span>
              )}
            </button>
          )}

        </div>

      </div>
    </div>
  );
};

export default React.memo(MobileMapToolsSheet);
